import React, {
  Suspense,
  lazy,
} from 'react'

import {
  createBrowserRouter,
  Navigate,
} from 'react-router-dom'

import Layout from '@/pages/Layout'
import Login from '@/pages/Login'
import Register from '@/pages/Register'

import {
  AuthRoute,
} from '@/components/AuthRoute'

import {
  ProductRoute,
} from '@/components/ProductRoute'

import RoleRoute from '@/components/RoleRoute'


// ======================================================
// Lazy Pages
// ======================================================

const Home =
  lazy(() =>
    import('@/pages/Home')
  )


const Profile =
  lazy(() =>
    import('@/pages/Profile')
  )


// ======================================================
// Administration
// ======================================================

const LotteryDrawManagement =
  lazy(() =>
    import('@/pages/LotteryDrawManagement')
  )


// ======================================================
// Lotto 6/49
// ======================================================

const Lotto649 =
  lazy(() =>
    import('@/pages/Lotto649')
  )


const Lotto649MyTickets =
  lazy(() =>
    import('@/pages/Lotto649/MyTickets')
  )


const Lotto649ScanTicket =
  lazy(() =>
    import('@/pages/Lotto649/ScanTicket')
  )


// ======================================================
// Lotto MAX
// ======================================================

const LottoMax =
  lazy(() =>
    import('@/pages/LottoMax')
  )


// ======================================================
// Gaming
// ======================================================

// ------------------------------------------------------
// Paid Roulette Analytics
// ------------------------------------------------------

const Roulette =
  lazy(() =>
    import('@/pages/Roulette')
  )


// ------------------------------------------------------
// Free Roulette Game
// ------------------------------------------------------

const RouletteGame =
  lazy(() =>
    import('@/pages/RouletteGame')
  )


// ======================================================
// Loading
// ======================================================

const PageLoading = () => (

  <div
    style={{
      minHeight: '240px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#64748b',
      fontSize: '13px',
    }}
  >

    Loading...

  </div>

)


// ======================================================
// Lazy Wrapper
// ======================================================

const LazyPage = ({
  children,
}) => (

  <Suspense
    fallback={
      <PageLoading />
    }
  >

    {children}

  </Suspense>

)


// ======================================================
// Product Routes
//
// Paid modules.
//
// All protected by ProductRoute.
//
// Administrator / OM:
//     Full access.
//
// Normal user:
//     Must have matching purchasedProducts.
// ======================================================

const productRoutes = [

  // ==================================================
  // Lotto 6/49
  //
  // /lotto-649
  // ==================================================

  {
    path:
      'lotto-649',

    productKey:
      '649',

    element:
      <Lotto649 />,
  },


  // ==================================================
  // Lotto 6/49
  //
  // My Tickets
  //
  // /lotto-649/my-tickets
  // ==================================================

  {
    path:
      'lotto-649/my-tickets',

    productKey:
      '649',

    element:
      <Lotto649MyTickets />,
  },


  // ==================================================
  // Lotto 6/49
  //
  // Scan Ticket
  //
  // /lotto-649/scan-ticket
  // ==================================================

  {
    path:
      'lotto-649/scan-ticket',

    productKey:
      '649',

    element:
      <Lotto649ScanTicket />,
  },


  // ==================================================
  // Lotto MAX
  //
  // /lotto-max
  // ==================================================

  {
    path:
      'lotto-max',

    productKey:
      'max',

    element:
      <LottoMax />,
  },


  // ==================================================
  // Roulette Analytics
  //
  // /roulette
  // ==================================================

  {
    path:
      'roulette',

    productKey:
      'roulette',

    element:
      <Roulette />,
  },

]


// ======================================================
// User Routes
//
// Available to every authenticated user.
// ======================================================

const userRoutes = [

  // ==================================================
  // Profile
  //
  // /profile
  // ==================================================

  {
    path:
      'profile',

    element:
      <Profile />,
  },


  // ==================================================
  // Free Roulette Game
  //
  // /roulette-game
  //
  // No ProductRoute required.
// ==================================================

  {
    path:
      'roulette-game',

    element:
      <RouletteGame />,
  },

]


// ======================================================
// Build Product Route
// ======================================================

const buildProductRoute = ({
  path,
  productKey,
  element,
}) => ({

  path,

  element: (

    <ProductRoute
      productKey={
        productKey
      }
    >

      <LazyPage>

        {element}

      </LazyPage>

    </ProductRoute>

  ),

})


// ======================================================
// Build User Route
// ======================================================

const buildUserRoute = ({
  path,
  element,
}) => ({

  path,

  element: (

    <LazyPage>

      {element}

    </LazyPage>

  ),

})


// ======================================================
// Build Admin Route
//
// Administrator / OM only.
// ======================================================

const buildAdminRoute = ({
  path,
  element,
}) => ({

  path,

  element: (

    <RoleRoute
      roles={[
        'Administrator',
        'OM',
      ]}
    >

      <LazyPage>

        {element}

      </LazyPage>

    </RoleRoute>

  ),

})


// ======================================================
// Router
// ======================================================

const router =
  createBrowserRouter([

    // ==================================================
    // Public Authentication Routes
    //
    // IMPORTANT:
    //
    // These routes MUST stay outside AuthRoute.
    //
    // A user does not need to be authenticated
    // in order to login or register.
    // ==================================================

    {
      path:
        '/login',

      element:
        <Login />,
    },


    {
      path:
        '/register',

      element:
        <Register />,
    },


    // ==================================================
    // Authenticated Application
    // ==================================================

    {

      path:
        '/',

      element: (

        <AuthRoute>

          <Layout />

        </AuthRoute>

      ),


      children: [

        // ==============================================
        // Home
        //
        // /
        // ==============================================

        {
          index:
            true,

          element: (

            <LazyPage>

              <Home />

            </LazyPage>

          ),

        },


        // ==============================================
        // Account & Free Tools
        //
        // /profile
        // /roulette-game
        // ==============================================

        ...userRoutes.map(
          buildUserRoute
        ),


        // ==============================================
        // Paid Products
        //
        // /lotto-649
        // /lotto-649/my-tickets
        // /lotto-649/scan-ticket
        // /lotto-max
        // /roulette
        // ==============================================

        ...productRoutes.map(
          buildProductRoute
        ),


        // ==============================================
        // Administration
        //
        // Administrator / OM only.
        //
        // /lottery-draws
        // ==============================================

        buildAdminRoute({

          path:
            'lottery-draws',

          element:
            <LotteryDrawManagement />,

        }),

      ],

    },


    // ==================================================
    // Fallback
    //
    // Any unknown route returns to Home.
    // ==================================================

    {

      path:
        '*',

      element: (

        <Navigate
          to="/"
          replace
        />

      ),

    },

  ])


export default router